import React, { useState } from "react";
import CommonButtons from "../../../../../shared/components/CommonButtons";
import { ModalWrapper } from "../../../../../shared/components/ModalWrapper";
import InputGroup from "src/business-types/grocery/layouts/setting/customer/InputGroup";

interface CustomerDialogProps {
  customerAdd: boolean;
  handleCloseCustomer: () => void;
  firstName: string;
  setFirstName: (value: string) => void;
  middleName: string;
  setMiddleName: (value: string) => void;
  lastName: string;
  setLastName: (value: string) => void;
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  handleSubmit: (customerId?: string) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
}

const CustomerDialog: React.FC<CustomerDialogProps> = ({
  customerAdd,
  handleCloseCustomer,
  firstName,
  setFirstName,
  middleName,
  setMiddleName,
  lastName,
  setLastName,
  phoneNumber,
  setPhoneNumber,
  handleSubmit,
  searchTerm,
  setSearchTerm,
}) => {
  const [showMoreFields, setShowMoreFields] = useState(false);

  if (!customerAdd) return null;

  const handleInputChange = (name: string, value: string) => {
    switch (name) {
      case "firstName":
        setFirstName(value);
        break;
      case "middleName":
        setMiddleName(value);
        break;
      case "lastName":
        setLastName(value);
        break;
      case "phoneNumber":
        const numericValue = value.replace(/\D/g, "").slice(0, 8);
        setPhoneNumber(numericValue);
        break;
      default:
        break;
    }
  };

  // Define all fields configuration
  const getFieldConfig = () => ({
    firstName: { label: "First Name", name: "firstName", value: firstName, mandatory: true, type: "text" },
    middleName: { label: "Middle Name", name: "middleName", value: middleName, mandatory: false, type: "text" },
    lastName: { label: "Last Name", name: "lastName", value: lastName, mandatory: false, type: "text" },
    phoneNumber: { label: "Mobile Number", name: "phoneNumber", value: phoneNumber, mandatory: true, type: "number" },
  });

  const fields = getFieldConfig();

  const renderInputGroup = (fieldKey: keyof typeof fields) => {
    const field = fields[fieldKey];
    return (
      <InputGroup 
        key={fieldKey}
        label={field.label}
        name={field.name}
        value={field.value}
        onChange={handleInputChange}
        mandatory={field.mandatory}
        type={field.type}
      />
    );
  };

  const isFormValid = 
    firstName.trim().length > 0 && 
    phoneNumber.trim().length === 8;

  return (
    <ModalWrapper title="Enter Customer Details" onClose={handleCloseCustomer} showCloseButton width="max-w-lg">
      <div className="space-y-4">
        {/* First Name with More button */}
        <div className="relative">
          {renderInputGroup("firstName")}
          <button
            type="button"
            onClick={() => setShowMoreFields(!showMoreFields)}
            className="absolute -bottom-6 right-0 text-sm text-blue-600 hover:text-blue-800 font-medium focus:outline-none"
          >
            {showMoreFields ? "- Less" : "+ More"}
          </button>
        </div>

        {/* Optional fields - shown when "More" is clicked */}
        {showMoreFields && (
          <>
            {renderInputGroup("middleName")}
            {renderInputGroup("lastName")}
          </>
        )}

        {/* Mobile Number - always shown */}
        {renderInputGroup("phoneNumber")}
      </div>

      <div className="flex justify-end items-center pt-6 gap-6">
        <CommonButtons label="Cancel" className="w-32" variant="outlineDanger" onClick={handleCloseCustomer} />
        <CommonButtons label="Save Customer" className="w-40" onClick={() => handleSubmit()} disabled={!isFormValid} />
      </div>
    </ModalWrapper>
  );
};

export default CustomerDialog;